import { formatPriceToman } from './formatPriceToman';
import { formatCloseDateLabel } from './dateUtils';
import { getDealStageVisualType } from './dealStageUtils';

const STATUS_FA = { won: 'موفق', lost: 'ناموفق', ongoing: 'در جریان' };

const getDealStageType = (deal) =>
  getDealStageVisualType(deal?.lead_stage?.name || deal?.lead_stage?.slug);

const formatClientDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('fa-IR');
};

const buildPromptTemplate = ({
  clientName,
  totalValue,
  dealsCount,
  firstDealDate,
  currentStage,
  lastStatus,
  notesSummary,
  dealName,
}) => {
  const dealLine = dealName ? `\nنام معامله: ${dealName}` : '';

  return `تو یک مشاور حرفه‌ای فروش و ارتباط با مشتری هستی. وظیفه تو این است که بر اساس اطلاعات زیر، بهترین و دقیق‌ترین سناریوی تماس یا پیگیری را طراحی کنی تا بیشترین احتمال پیشرفت معامله و ایجاد اعتماد با مشتری ایجاد شود.

اطلاعات مشتری:

نام مشتری: ${clientName || '—'}${dealLine}

مجموع مبالغ معاملات قبلی: ${totalValue}

تعداد معاملات قبلی: ${dealsCount}

تاریخ اولین معامله: ${firstDealDate}

مرحله معامله جاری: ${currentStage}

وضعیت آخرین معامله: ${lastStatus}

آخرین اقدام انجام شده: —

خلاصه توضیحات مهم درباره مشتری:

${notesSummary}

با توجه به اطلاعات بالا:

ابتدا وضعیت این مشتری را تحلیل کن و بگو در چه موقعیتی از چرخه فروش قرار دارد.
بهترین استراتژی تماس با این مشتری را مشخص کن.
یک سناریوی کامل تماس تلفنی بنویس که شامل موارد زیر باشد:
شروع حرفه‌ای تماس
ایجاد ارتباط و یادآوری تعامل قبلی
ورود طبیعی به موضوع تماس
توضیح متناسب با مرحله معامله
دو واکنش احتمالی مشتری و پاسخ مناسب به آن‌ها
جمع‌بندی مکالمه
پیشنهاد گام بعدی
سناریو باید کاملاً طبیعی، حرفه‌ای و قابل استفاده مستقیم توسط اپراتور باشد.

در پایان نیز یک نسخه کوتاه از سناریوی تماس در 3 تا 4 جمله ارائه بده که برای تماس سریع قابل استفاده باشد.`;
};

/** پرامپت سناریوی تماس برای مشتری با چند معامله */
export const buildClientConsultantPrompt = ({ clientName, deals, stats }) => {
  const dealList = Array.isArray(deals) ? deals : [];
  const totalValue = formatPriceToman(dealList.reduce((sum, d) => sum + (Number(d.value) || 0), 0));
  const dealsCount = stats?.total ?? dealList.length;
  const firstDealDateRaw =
    stats?.first_won_date ||
    dealList
      .map((d) => d.close_date || d.created_at)
      .filter(Boolean)
      .sort()[0];
  const firstDealDate = firstDealDateRaw
    ? formatCloseDateLabel(firstDealDateRaw) || formatClientDate(firstDealDateRaw)
    : '—';
  const currentDeal = dealList[0] || {};
  const currentStage = currentDeal.lead_stage?.name || '—';
  const lastStatus = STATUS_FA[getDealStageType(currentDeal)] || 'در جریان';
  const notesSummary =
    dealList
      .filter((d) => d.note && String(d.note).trim() !== '')
      .map((d) => `- ${d.name || 'معامله'}: ${String(d.note).trim()}`)
      .join('\n') || '—';

  return buildPromptTemplate({
    clientName,
    totalValue,
    dealsCount,
    firstDealDate,
    currentStage,
    lastStatus,
    notesSummary,
  });
};

/** پرامپت سناریوی تماس برای یک معامله */
export const buildDealConsultantPrompt = ({ deal, clientName }) => {
  if (!deal) return '';

  const stage = deal.lead_stage?.name || '—';
  const status = STATUS_FA[getDealStageType(deal)] || 'در جریان';
  const value = formatPriceToman(Number(deal.value) || 0);
  const note = deal.note && String(deal.note).trim() ? String(deal.note).trim() : '—';
  const dealName = deal.name || '—';
  const dateRaw = deal.close_date || deal.created_at;
  const dealDate = dateRaw ? formatCloseDateLabel(dateRaw) || formatClientDate(dateRaw) : '—';

  return buildPromptTemplate({
    clientName,
    totalValue: value,
    dealsCount: 1,
    firstDealDate: dealDate,
    currentStage: stage,
    lastStatus: status,
    notesSummary: note,
    dealName,
  });
};
